import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import { WidgetItem } from '../../../models/widgets/widget-item';
import { WidgetItemComponent } from '../../widget-item/widget-item.component';
import { WidgetTitleComponent } from '../../widget-title/widget-title.component';
import { ProfileAvatarComponent } from '../profile/profile-avatar/profile-avatar.component';
import { Common, ModalService, ToastService } from '@meplis/services';
import { GroupCreateModalComponent } from '../../groups/group-create-modal/group-create-modal.component';
import { ProfileService } from '@cnp/services';
import { ApiSocialV2Service } from '@cnp/services';
import {
  CommonHelper,
  EntityInstance,
  Group,
  PaginationOptions,
  ProfileEntity,
  SocialNetworkContentType,
  SocialNetworkInstance,
  SocialNetworkThreadOwnerType,
  ThreadsSearch,
} from '@meplis/common';
import { SkeletonDirective } from '@meplis/directives';
import { AuthenticationService } from '../../../services/application/authentication.service';
import { forkJoin, Subscription, switchMap } from 'rxjs';
import { SocialService } from '../../../services/application/social.service';
import { AppRoutes } from '@cnp/common';
import { SocialEvent } from '@cnp/common';
import { GroupService } from '@cnp/services';
import { ProfileDisplayNamePipe } from '@cnp/pipes';
import { Router } from '@angular/router';
import { ProfileCompletenessComponent } from '../profile/profile-completeness/profile-completeness.component';

@Component({
  selector: 'app-left-sidebar',
  imports: [
    ProfileAvatarComponent,
    WidgetTitleComponent,
    WidgetItemComponent,
    TranslatePipe,
    ProfileAvatarComponent,
    SkeletonDirective,
    ProfileDisplayNamePipe,
    ProfileCompletenessComponent],
  templateUrl: './left-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './left-sidebar.component.scss',
})
export class LeftSidebarComponent implements OnInit, OnDestroy {
  private _modalService = inject(ModalService);
  private _profileService = inject(ProfileService);
  private _apiSocialV2Service = inject(ApiSocialV2Service);
  private _toastService = inject(ToastService);
  private _authService = inject(AuthenticationService);
  private _socialService = inject(SocialService);
  private _groupService = inject(GroupService);
  private _router = inject(Router);

  public profile: ProfileEntity;
  public userInstance: EntityInstance;
  public profileBackgroundImageUrl: string = 'images/profile-background.webp';

  public loadingGroups: boolean = false;
  public collaborationGroups: WidgetItem[] = [];
  public hasMyGroups: boolean = false;

  public loadingEvents: boolean = false;
  public liveStreamings: WidgetItem[] = [];
  public hasMyEvents: boolean = false;

  public profileSubscription: Subscription;
  public userInstanceSubscription: Subscription;
  public authSubscription: Subscription;
  public groupsSubscription: Subscription;
  public profileChangedSubscription: Subscription;
  public groupChangedSubscription: Subscription;

  constructor() {
    this.profileSubscription = this._profileService
      .getProfile()
      .subscribe((profile) => {
        this.profile = profile;
      });

    this.userInstanceSubscription = this._socialService
      .getUserInstance()
      .subscribe((userInstance) => {
        this.userInstance = userInstance;
        this._handleInit();
      });

    this.authSubscription = this._authService.onLoggedInOut
      .pipe(
        switchMap(() =>
          forkJoin({
            profile: this._profileService.getProfile(),
            userInstance: this._socialService.getUserInstance(),
          })
        )
      )
      .subscribe(({ profile, userInstance }) => {
        this.userInstance = userInstance;
        this.profile = profile;
        this._handleInit();
      });

    this.profileChangedSubscription = this._profileService.onProfileChanged
      .pipe(
        switchMap(() => {
          this._socialService.clearUserInstance();
          return forkJoin({
            profile: this._profileService.getProfile(),
            userInstance: this._socialService.getUserInstance(),
          });
        })
      )
      .subscribe(({ profile, userInstance }) => {
        this.profile = profile;
        this.userInstance = userInstance;
      });

    this.groupChangedSubscription = this._groupService.groupChanged.subscribe(
      () => this.loadGroups()
    );
  }
  ngOnDestroy(): void {
    this.profileSubscription?.unsubscribe();
    this.userInstanceSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
    this.groupsSubscription?.unsubscribe();
    this.profileChangedSubscription?.unsubscribe();
    this.groupChangedSubscription?.unsubscribe();
  }

  public ngOnInit(): void {}

  private _handleInit(): void {
    if (!this._authService.isLogged) {
      return;
    }

    // probe whether user has personal groups/events (using small page size)
    forkJoin({
      groups: this._apiSocialV2Service.getMyGroups(this.userInstance.token, {
        page: 0,
        limit: 1,
      }),
      events: this._apiSocialV2Service.getMyEvents(this.userInstance.token, {
        page: 0,
        limit: 1,
      }),
    }).subscribe({
      next: ({ groups, events }) => {
        this.hasMyGroups = !!groups?.length;
        this.hasMyEvents = !!events?.length;
        this.loadEvents();
        this.loadGroups();
      },
      error: () => {
        // if probe fails, fall back to default loading
        this.loadEvents();
        this.loadGroups();
      },
    });
  }

  private loadEvents(): void {
    this.loadingEvents = true;

    const eventsPagination: PaginationOptions = {
      page: 0,
      limit: 5,
    };

    const obs = this.hasMyEvents
      ? this._apiSocialV2Service.getMyEvents(
          this.userInstance.token,
          eventsPagination
        )
      : this._apiSocialV2Service.getUpcomingEvents(
          this.userInstance.token,
          eventsPagination
        );

    obs.subscribe({
      next: (events) => {
        this.liveStreamings = events.map((result) => {
          const event = result as SocialEvent;

          let actionRoute = '';

          if (event.instance?.ownerType === SocialNetworkThreadOwnerType.HUB) {
            const hubSlug = event.instance.slug || event.instance.ownerId;
            const eventSlug =
              event.slug || event.title?.toLowerCase().replace(/\s+/g, '-');
            actionRoute = CommonHelper.buildFullRouteUrl(
              AppRoutes['HUB_EVENT'].fullPath,
              [hubSlug, event.instance.ownerId, eventSlug, event.token],
              [':slug', ':id', ':eventSlug', ':eventId']
            );
          } else {
            actionRoute = CommonHelper.buildFullRouteUrl(
              AppRoutes['EVENT'].fullPath,
              [event.token]
            );
          }

          return {
            title: event.title,
            description:
              CommonHelper.formatDate(CommonHelper.zonedWallClockToInstant(event.startsAt, event.timezone)) || '',
            actionTitle: 'EVENTS.VIEW_EVENT',
            actionRoute,
          };
        });

        this.loadingEvents = false;
      },
      error: (error) => {
        this._toastService.showError(error);
        this.loadingEvents = false;
      },
    });
  }

  public loadGroups(): void {
    this.loadingGroups = true;

    const pagination: PaginationOptions = {
      page: 0,
      limit: 4,
    };

    const obs = this.hasMyGroups
      ? this._apiSocialV2Service.getMyGroups(
          this.userInstance.token,
          pagination
        )
      : this._apiSocialV2Service.getThreads(
          pagination,
          SocialNetworkContentType.COLLABORATION_GROUP
        );

    this.groupsSubscription = obs.subscribe({
      next: (groups: Group[] | ThreadsSearch[]) => {
        this.collaborationGroups = groups.map((group) => {
          let actionRoute = `/groups/${group.token}`;

          const groupResult = group as unknown as Group;

          if (groupResult.hub) {
            const hubSlug = groupResult.hub.slug || groupResult.hub.ownerId;
            actionRoute = CommonHelper.buildFullRouteUrl(
              AppRoutes['HUB_GROUP_FEED'].fullPath,
              [hubSlug, groupResult.hub.token, groupResult.token],
              [':slug', ':id', ':groupId']
            );
          }

          return {
            title: group.title,
            description: `${group.numberOfMembers} collaborators`,
            actionTitle: 'GROUP.SEE_GROUP',
            actionRoute,
          };
        });

        this.loadingGroups = false;
      },
      error: (error) => {
        this._toastService.showError(error);
        this.loadingGroups = false;
      },
    });
  }

  public goToProfile(): void {
    this._router.navigate([AppRoutes['PROFILE'].fullPath]);
  }

  public openCreateGroupModal(): void {
    this._modalService.openModal(GroupCreateModalComponent);
  }
}
